/**
 * Support API — PUBLIC (unauthenticated) support requests
 *
 * POST /v1/support/public-requests    (no auth, no school header)
 *
 * Backs the marketing-site support form at `schooljourney.com/support` and its
 * school-branded twin `schooljourney.com/support/<school-slug>`. Lands in the
 * SAME place as the authenticated in-app intake (`POST /v1/support/requests`):
 * a Triage-state issue in the SCH team, tagged with the same type label, linked
 * to the school's Linear customer as a need when a school can be resolved.
 *
 * Differences from the authenticated contract, all forced by "public":
 * - `name` + `email` are carried in the body because there is NO server-side
 *   identity to source them from. They are therefore UNVERIFIED and the backend
 *   labels them as such inside the issue body — never treat them as an identity.
 * - `schoolSlug` is OPTIONAL and advisory. Absent, unknown, or ambiguous → the
 *   submission still lands, as a generic School Journey issue with no customer
 *   link. It must never fail the submission.
 * - `_hp` is a honeypot. Real users never see or fill it; bots that auto-fill
 *   every input do. A filled honeypot is answered with the same success body as
 *   a real submission so the bot learns nothing. Named `_hp` to match the
 *   existing public-form convention in this repo.
 * - The response deliberately carries NO Linear issue identifier — an
 *   unauthenticated caller has no business learning our internal issue keys.
 */

import { z } from 'zod'
import { SupportRequestTypeSchema } from './requests'

// ============================================================================
// POST /v1/support/public-requests
// ============================================================================

/** Ceiling on the honeypot value we will even parse — a bot can't use it as a payload channel. */
export const MAX_SUPPORT_HONEYPOT_LENGTH = 200

export const CreatePublicSupportRequestParamsSchema = z.object({
  /** Same three-way enum as the in-app intake, so both feed one type label set. */
  type: SupportRequestTypeSchema,
  /** Submitter's name. UNVERIFIED — client-supplied on a public endpoint. */
  name: z.string().trim().min(1).max(120),
  /** Reply-to address. UNVERIFIED — client-supplied on a public endpoint. */
  email: z.string().trim().email().max(320),
  /**
   * Optional school slug, present when the form was opened at
   * `/support/<school-slug>`. Advisory only: an unknown or ambiguous slug
   * degrades to a generic (customer-less) issue rather than failing.
   */
  schoolSlug: z.string().trim().min(1).max(120).optional(),
  message: z.string().trim().min(1).max(5000),
  /** Honeypot. Must stay empty; any value means "bot" and the request is dropped silently. */
  _hp: z.string().max(MAX_SUPPORT_HONEYPOT_LENGTH).optional()
})

export type CreatePublicSupportRequestParams = z.infer<typeof CreatePublicSupportRequestParamsSchema>

/**
 * Success body. Identical for a genuine submission and for a honeypot-tripped
 * one — the two are indistinguishable to the caller by design.
 */
export const CreatePublicSupportRequestResponseSchema = z.object({
  submitted: z.literal(true)
})

export type CreatePublicSupportRequestResponse = z.infer<typeof CreatePublicSupportRequestResponseSchema>
